import { useTranslation } from 'react-i18next';
import { ArrowDownRight, AlertTriangle, CheckCircle2, Building2, User, UserCog, Droplet } from 'lucide-react';
import { computeTripPayout, formatSar, type RateUnit } from '@/lib/trip-math';

interface LiveMathPreviewProps {
  /** Current delivered quantity input (m³, ton, km, etc.) */
  deliveredQty: number;
  /** Contract target quantity */
  targetQty: number;
  /** What client pays at full delivery */
  clientRateAtTarget: number;
  /** What investor earns at full delivery */
  investorRateAtTarget: number;
  /** What driver earns at full delivery */
  driverRateAtTarget: number;
  /** Rate unit (informs binary vs proportional math) */
  rateUnit?: RateUnit;
  /** Hide title (when used inside another card) */
  compact?: boolean;
  /** PR-CONTRACT-WATER MAY 11 — water filling station rate per m³ (SAR, excl VAT). */
  waterRatePerM3?: number | null;
  /** Optional station code/name to render under the row. */
  waterStationLabel?: string | null;
}

/**
 * Live trip-payout breakdown.
 *
 * Recomputes instantly client-side as inputs change. Mirrors the backend
 * algorithm in lib/trip-math.ts. Used in:
 *   - Contract creation form (preview rates while configuring)
 *   - Trip creation form later (preview earnings before saving)
 *   - Reports (verify historical math)
 */
export function LiveMathPreview(props: LiveMathPreviewProps) {
  const { t } = useTranslation();
  const { deliveredQty, targetQty, clientRateAtTarget, investorRateAtTarget, driverRateAtTarget, rateUnit, compact, waterRatePerM3, waterStationLabel } = props;

  const result = computeTripPayout({
    deliveredQty,
    targetQty,
    clientRateAtTarget,
    investorRateAtTarget,
    driverRateAtTarget,
    rateUnit,
  });
  // PR-CONTRACT-WATER MAY 11 — water cost as an additional company-side
  // deduction. Conservative approach: subtract from companyKeep without
  // changing the driver/investor split logic.
  const waterCost = (waterRatePerM3 ?? 0) * deliveredQty;
  const companyKeepAfterWater = result.companyKeep - waterCost;

  const hasInputs = targetQty > 0 && clientRateAtTarget > 0;
  const fractionPct = (result.fraction * 100).toFixed(1);

  if (!hasInputs) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-muted/20 p-6 text-center">
        <p className="text-sm text-muted-foreground">
          {t('contracts.preview.fillInputs')}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      {!compact && (
        <div className="bg-muted/30 px-4 py-3 border-b border-border">
          <h4 className="text-xs uppercase tracking-widest text-muted-foreground font-medium">
            {t('contracts.preview.title')}
          </h4>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {t('contracts.preview.subtitle')}
          </p>
        </div>
      )}

      {/* Delivery scenario */}
      <div className="px-4 py-3 border-b border-border bg-muted/10">
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">{t('contracts.preview.scenario')}</span>
          <span className={`font-mono text-sm tabular-nums ${result.fraction === 1 ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
            {deliveredQty} / {targetQty} {rateUnit === 'per_m3' ? 'm³' : rateUnit === 'per_ton' ? 't' : rateUnit === 'per_km' ? 'km' : ''}
            <span className="ms-2 text-xs opacity-70">({fractionPct}%)</span>
          </span>
        </div>
      </div>

      {/* Client → Company */}
      <div className="px-4 py-3 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ArrowDownRight className="h-4 w-4 text-emerald-500" />
            <span className="text-sm">{t('contracts.preview.clientPays')}</span>
          </div>
          <span className="font-mono text-base font-semibold text-emerald-600 dark:text-emerald-400 tabular-nums">
            {formatSar(result.clientPayment, { showCurrency: false })}
          </span>
        </div>
        {result.losses.client > 0 && (
          <p className="text-[11px] text-muted-foreground mt-1 ms-6">
            {t('contracts.preview.clientCutLine', { amount: formatSar(result.losses.client) })}
          </p>
        )}
      </div>

      {/* Distribution */}
      <div className="divide-y divide-border">
        {/* PR-CONTRACT-WATER MAY 11 — water cost row when station rate is provided */}
        {waterCost > 0 && (
          <PreviewRow
            icon={<Droplet className="h-4 w-4" />}
            label={'Water cost'}
            value={-waterCost}
            loss={0}
            color="text-sky-600 dark:text-sky-400"
            subtext={waterStationLabel ? `${waterStationLabel} @ ${(waterRatePerM3 ?? 0).toFixed(3)} SAR/m³` : `${(waterRatePerM3 ?? 0).toFixed(3)} SAR/m³ × ${deliveredQty}`}
          />
        )}
        {/* Company keeps */}
        <PreviewRow
          icon={<Building2 className="h-4 w-4" />}
          label={t('contracts.preview.companyKeep')}
          value={companyKeepAfterWater}
          loss={result.losses.company}
          color="text-foreground"
          highlight={true}
        />
        {/* Investor net */}
        <PreviewRow
          icon={<User className="h-4 w-4" />}
          label={t('contracts.preview.investorNet')}
          value={result.investorNet}
          loss={result.losses.investor}
          color="text-blue-600 dark:text-blue-400"
          subtext={t('contracts.preview.investorNetSubtext', {
            gross: formatSar(result.investorGross, { showCurrency: false }),
            driver: formatSar(result.driverPay, { showCurrency: false }),
          })}
        />
        {/* Driver pay */}
        <PreviewRow
          icon={<UserCog className="h-4 w-4" />}
          label={t('contracts.preview.driverPay')}
          value={result.driverPay}
          loss={result.losses.driver}
          color="text-purple-600 dark:text-purple-400"
          subtext={t('contracts.preview.driverPaidByCompany')}
        />
      </div>

      {/* Validation footer */}
      <div className={`px-4 py-2.5 border-t border-border text-xs flex items-center gap-2 ${
        result.valid
          ? 'bg-emerald-500/5 text-emerald-700 dark:text-emerald-400'
          : 'bg-destructive/5 text-destructive'
      }`}>
        {result.valid ? (
          <>
            <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
            <span>{t('contracts.preview.validated', {
              total: formatSar(result.totalDistributed, { showCurrency: false }),
              client: formatSar(result.clientPayment, { showCurrency: false }),
            })}</span>
          </>
        ) : (
          <>
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            <span>{result.warnings[0] ?? t('contracts.preview.invalid')}</span>
          </>
        )}
      </div>
    </div>
  );
}

function PreviewRow({
  icon, label, value, loss, color, subtext, highlight,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  loss: number;
  color: string;
  subtext?: string;
  highlight?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className={`px-4 py-3 ${highlight ? 'bg-gold/5' : ''}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={color}>{icon}</span>
          <span className="text-sm">{label}</span>
        </div>
        <span className={`font-mono text-base font-semibold ${color} tabular-nums`}>
          {formatSar(value, { showCurrency: false })}
        </span>
      </div>
      <div className="ms-6 mt-0.5 space-y-0.5">
        {subtext && (
          <p className="text-[11px] text-muted-foreground">{subtext}</p>
        )}
        {loss > 0 && (
          <p className="text-[11px] text-amber-600 dark:text-amber-400">
            {t('contracts.preview.lossLine', { amount: formatSar(loss) })}
          </p>
        )}
      </div>
    </div>
  );
}
